import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import './ManagerSummaryApp.css';
import ManagedDisclaimer from '../../components/ManagedDisclaimer';

// IC-deck / Manager Summary workbook tables, plus one table per tailored client series
// (the Portfolio Viewer's "Tailored" tab: Altura / Compass / Derwent / Horizon / Pathway …),
// reached through a "Tailored" tab that opens a row of series tabs. Returns come straight
// from the AP List; the Alpha toggle shows fund minus its Drummond-assigned benchmark for
// the same period. The Excel download lays every table out like the workbook tabs.

const PERIODS = ['1m', '3m', '6m', '12m', '3yr', '5yr'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const SOURCE_NOTE = 'Data source: Refinitiv. Performance data greater than 1 year are annualised. Performance data '
  + 'is the history of the individual funds and does not consider the holding period in Drummond portfolios.';

// Disclaimer text is managed in Admin > Disclaimers (tool_manager_summary).

// '2026-08-31' -> '31 August 2026'
const longDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return MONTHS[(+m) - 1] ? `${+d} ${MONTHS[(+m) - 1]} ${y}` : iso;
};
// '2026-08-31' -> 'August 2026' (dropdown labels)
const monthLabel = (iso) => {
  const [y, m] = String(iso).split('-');
  return MONTHS[(+m) - 1] ? `${MONTHS[(+m) - 1]} ${y}` : iso;
};
// One decimal; values that round to zero print as "0.0" (never "-0.0"); alpha gets a "+" sign.
const fmt = (v, signed = false, dp = 1) => {
  if (v === null || v === undefined) return '–';
  const eps = 0.5 * (10 ** -dp);
  const n = Math.abs(Number(v)) < eps ? 0 : Number(v);
  const t = n.toFixed(dp);
  return signed && n > 0 ? `+${t}` : t;
};

// One asset-class block: fund rows, then the benchmark rows those funds are measured against.
function GroupRows({ group, layout, mode, headerAsOf }) {
  const priv = layout === 'private';
  const ncols = PERIODS.length + (priv ? 5 : 4);
  const rows = [];
  group.funds.forEach((f, i) => {
    const vals = mode === 'alpha' ? f.alpha : f.returns;
    const lag = f.as_at && headerAsOf && f.as_at !== headerAsOf;
    rows.push(
      <tr key={`f-${f.tr_code}`} className="ms-fund" title={`${f.tr_code}${f.apir_code ? ' · ' + f.apir_code : ''}\nHeld in: ${f.held_in.join(', ')}`}>
        <td className="ms-l ms-group">{i === 0 ? group.group : ''}</td>
        <td className="ms-l ms-name">
          {f.name}
          {!f.in_ap_list && <span className="ms-flag" title="Held but not in the AP List">not in AP List</span>}
          {lag && <sup className="ms-lag" title={`Returns as at ${longDate(f.as_at)}`}>†</sup>}
        </td>
        {PERIODS.map((p) => {
          const v = vals[p];
          const cls = mode === 'alpha' && v !== null && v !== undefined
            ? (v >= 0 ? ' ms-pos' : ' ms-neg') : '';
          return <td key={p} className={`ms-num${cls}`}>{fmt(v, mode === 'alpha')}</td>;
        })}
        {priv
          ? <><td className="ms-num">{fmt(f.sharpe, false, 2)}</td><td className="ms-num">{fmt(f.std_dev)}</td></>
          : <td className="ms-num">{fmt(f.tracking_error)}</td>}
        <td className="ms-l ms-bench-name">{f.benchmark_name || (f.benchmark_ticker ? f.benchmark_ticker : '–')}</td>
      </tr>,
    );
  });
  group.benchmarks.forEach((b) => {
    rows.push(
      <tr key={`b-${b.tr_code}`} className="ms-benchmark" title={b.tr_code}>
        <td className="ms-l ms-group" />
        <td className="ms-l ms-name">
          {b.name || b.tr_code}
          {!b.in_ap_list && <span className="ms-flag" title="Benchmark not in the AP List">not in AP List</span>}
        </td>
        {PERIODS.map((p) => <td key={p} className="ms-num">{fmt(b.returns[p])}</td>)}
        {priv ? <><td className="ms-num">–</td><td className="ms-num">–</td></> : <td className="ms-num">–</td>}
        <td className="ms-l ms-bench-name">benchmark</td>
      </tr>,
    );
  });
  rows.push(<tr key={`sp-${group.group}`} className="ms-spacer"><td colSpan={ncols} /></tr>);
  return rows;
}

// "All" tab: every AP List fund with Shadow exposure across the master portfolios (deck tables
// less CFS FirstChoice, plus the tailored series), where it is held, and whether only tailored.
function AllFunds({ month }) {
  const [all, setAll] = useState(null);
  const [error, setError] = useState(null);
  const [onlyTailored, setOnlyTailored] = useState(false);
  const [exporting, setExporting] = useState(false);

  const downloadExcel = async () => {
    setExporting(true); setError(null);
    try {
      const r = await api.get('/api/manager-summary/all-funds.xlsx',
        { params: month ? { month } : {}, responseType: 'blob' });
      const m = /filename="([^"]+)"/.exec(r.headers?.['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'Manager Summary - All Funds.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* keep message */ }
      }
      setError(`Excel export failed: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let live = true;
    setAll(null); setError(null);
    api.get('/api/manager-summary/all-funds', { params: month ? { month } : {} })
      .then((r) => { if (live) setAll(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || e.message); });
    return () => { live = false; };
  }, [month]);

  if (error && !all) return <div className="ms-alert">{error}</div>;
  if (!all) return <p className="ms-muted">Loading all funds…</p>;

  const funds = onlyTailored ? all.funds.filter((f) => f.tailored_only) : all.funds;
  return (
    <>
      <div className="ms-all-actions">
        <button className="ms-btn-sm" disabled={exporting} onClick={downloadExcel}
                title="All Funds sheet plus a Fund by Portfolio sheet (one row per fund per portfolio)">
          {exporting ? 'Exporting…' : 'Download Excel'}
        </button>
      </div>
      {error && <div className="ms-alert">{error}</div>}
      {all.warnings.length > 0 && (
        <div className="ms-warn">{all.warnings.map((w) => <div key={w}>{w}</div>)}</div>
      )}
      <div className="ms-export">
        <div className="ms-export-title">All funds held – Shadow exposure as at {longDate(all.holdings_as_of)}</div>
        <div className="ms-caption">
          {all.fund_count} funds · {all.tailored_only_count} held only in tailored portfolios · master portfolios across{' '}
          {all.suites.length} suites (excludes {all.excluded_tables.join(', ')})
          <label className="ms-all-filter">
            <input type="checkbox" checked={onlyTailored} onChange={(e) => setOnlyTailored(e.target.checked)} />
            Tailored only
          </label>
        </div>
        <div className="ms-table-scroll">
          <table className="ms-table ms-all">
            <thead>
              <tr>
                <th className="ms-l ms-name">Fund</th>
                <th className="ms-l">APIR code</th>
                <th className="ms-l">Asset class group</th>
                <th className="ms-l">Held in</th>
                <th>Tailored only</th>
              </tr>
            </thead>
            <tbody>
              {funds.map((f) => (
                <tr key={f.apir_code} className="ms-fund">
                  <td className="ms-l ms-name">{f.name}</td>
                  <td className="ms-l ms-code">{f.apir_code}</td>
                  <td className="ms-l">{f.asset_class_group || '–'}</td>
                  <td className="ms-l ms-held">
                    {f.held_in.map((h) => (
                      <span key={h.suite} className={`ms-suite-chip${h.tailored ? ' ms-suite-tailored' : ''}`}
                            title={`${h.client || 'No client assigned'} · ${h.suite}\n`
                              + h.portfolios.map((p) => `${p.name} (${p.code})`).join('\n')}>
                        {h.client && <span className="ms-client">{h.client}</span>}
                        {h.suite}{h.portfolios.length > 1 ? ` (${h.portfolios.length})` : ''}
                      </span>
                    ))}
                  </td>
                  <td className="ms-c">{f.tailored_only ? <span className="ms-yes">Yes</span> : 'No'}</td>
                </tr>
              ))}
              {funds.length === 0 && (
                <tr className="ms-empty"><td colSpan={5}>No funds.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="ms-foot">
          <div>Funds are AP List rows whose APIR code has a Shadow_Data floating target weight above zero at each
            portfolio&apos;s latest period{all.restated ? ` on or before ${longDate(all.month)}` : ''}. Hover a suite to see its portfolios.</div>
        </div>
      </div>
    </>
  );
}

export default function ManagerSummaryApp() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('cfs');
  const [mode, setMode] = useState('returns');
  const [month, setMonth] = useState('');   // '' = latest month (AP List figures)
  const [exporting, setExporting] = useState(false);

  // Fetch whenever the month changes; loading/error are reset in the change handler.
  useEffect(() => {
    let live = true;
    api.get('/api/manager-summary/tables', { params: month ? { month } : {} })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [month]);

  const changeMonth = (value, latest) => {
    setLoading(true); setError(null);
    setMonth(value === latest ? '' : value);
  };

  // Excel workbook laid out like Manager Summary.xlsx (the five deck tabs, current month +
  // mode). The tailored series are added only while a Tailored tab is the one on screen.
  const downloadExcel = async (withTailored) => {
    setExporting(true); setError(null);
    try {
      const r = await api.get('/api/manager-summary/export.xlsx',
        { params: { mode, ...(withTailored ? { include_tailored: true } : {}), ...(month ? { month } : {}) },
          responseType: 'blob' });
      const disposition = r.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(disposition);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'Manager Summary.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* keep message */ }
      }
      setError(`Excel export failed: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  // Deck tables form the main tab strip; tailored series sit behind a "Tailored" tab as a
  // second row of series tabs (the viewer's series chips). A key that no longer exists
  // (a series removed from the registry) falls back to the first table.
  const deckTables = useMemo(() => (data?.tables || []).filter((t) => !t.tailored), [data]);
  const tailoredTables = useMemo(() => (data?.tables || []).filter((t) => t.tailored), [data]);
  const table = useMemo(
    () => data?.tables.find((t) => t.key === tab) || data?.tables[0] || null,
    [data, tab],
  );

  // Funds whose AP List as-at date lags the header date get a dagger and a footnote.
  const lagDates = useMemo(() => {
    if (!table) return [];
    const s = new Set();
    table.groups.forEach((g) => g.funds.forEach((f) => {
      if (f.as_at && data.as_of && f.as_at !== data.as_of) s.add(f.as_at);
    }));
    return [...s].sort();
  }, [table, data]);

  if (error && !data) return <div className="ms-app"><div className="ms-alert">{error}</div></div>;
  if (!data) return <div className="ms-app"><p className="ms-muted">Loading…</p></div>;

  const showAll = tab === 'all';
  const priv = table.layout === 'private';
  const title = `Manager Summary – ${longDate(data.as_of)} (${table.title})`;

  return (
    <div className="ms-app">
      <div className="ms-head">
        <h2>Manager Summary</h2>
        <span className="ms-muted">
          Returns to {longDate(data.as_of)} · AP List refreshed {longDate(data.ap_list_refreshed)} · holdings as at {longDate(table.holdings_as_of)}
          {loading && ' · loading…'}
        </span>
      </div>
      {error && <div className="ms-alert">{error}</div>}

      <div className="ms-toolbar">
        <div className="ms-tabs">
          {deckTables.map((t) => (
            <button key={t.key} className={`ms-tab${!showAll && t.key === table.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
              {t.title}
            </button>
          ))}
          {tailoredTables.length > 0 && (
            <button className={`ms-tab ms-tab-tailored${!showAll && table.tailored ? ' active' : ''}`}
                    onClick={() => setTab(tailoredTables[0].key)}
                    title={`${tailoredTables.length} tailored client series`}>
              Tailored
            </button>
          )}
          <button className={`ms-tab${showAll ? ' active' : ''}`} onClick={() => setTab('all')}
                  title="Every fund held across the master portfolios">
            All
          </button>
        </div>
        <div className="ms-right">
          <label className="ms-field">
            Month
            <select value={month || data.latest_month} onChange={(e) => changeMonth(e.target.value, data.latest_month)}>
              {data.months.map((m) => (
                <option key={m} value={m}>{monthLabel(m)}{m === data.latest_month ? ' (latest)' : ''}</option>
              ))}
            </select>
          </label>
          {!showAll && (
            <>
              <div className="ms-toggle" role="group" aria-label="Returns or alpha">
                <button className={mode === 'returns' ? 'active' : ''} onClick={() => setMode('returns')}>Returns</button>
                <button className={mode === 'alpha' ? 'active' : ''} onClick={() => setMode('alpha')}>Alpha</button>
              </div>
              <button className="ms-btn-sm" disabled={exporting} onClick={() => downloadExcel(table.tailored)}
                      title={table.tailored
                        ? 'The five workbook tables plus one tab per tailored client series'
                        : 'The five workbook tables, laid out like Manager Summary.xlsx (switch to Tailored to include the client series)'}>
                {exporting ? 'Exporting…' : 'Download Excel'}
              </button>
            </>
          )}
        </div>
      </div>

      {showAll && <AllFunds month={month} />}

      {/* Tailored: one series tab per client series (Altura / Compass / Derwent / Horizon / Pathway …) */}
      {!showAll && table.tailored && (
        <div className="ms-series" role="tablist" aria-label="Tailored client series">
          {tailoredTables.map((t) => (
            <button key={t.key} className={`ms-series-tab${t.key === table.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                    title={`${t.portfolio_codes.length} portfolio${t.portfolio_codes.length === 1 ? '' : 's'}: ${t.portfolio_codes.join(', ')}`}>
              {t.title}
              <span className="ms-series-count">{t.portfolio_codes.length}</span>
            </button>
          ))}
        </div>
      )}

      {!showAll && data.warnings.length > 0 && (
        <div className="ms-warn">
          {data.warnings.map((w) => <div key={w}>{w}</div>)}
        </div>
      )}

      {!showAll && <div className="ms-export">
        <div className="ms-export-title">{title}</div>
        <div className="ms-caption">
          {mode === 'alpha'
            ? 'Alpha vs Drummond-assigned benchmark (%): fund return less benchmark return for the same period'
            : 'Total return (%)'}
          {' · '}portfolios: {table.portfolio_codes.join(', ')}
          {data.restated && (
            <span className="ms-restated"> · restated at {longDate(data.month)} from monthly cumulative returns; holdings as at {longDate(table.holdings_as_of)}; current benchmark assignment</span>
          )}
        </div>
        <div className="ms-table-scroll">
          <table className="ms-table">
            <thead>
              <tr>
                <th className="ms-l ms-group">Asset class</th>
                <th className="ms-l ms-name">Fund</th>
                {PERIODS.map((p) => <th key={p}>{p}</th>)}
                {priv ? <><th>Sharpe ratio</th><th>Std. dev.</th></> : <th>Tracking error</th>}
                <th className="ms-l">Drummond benchmark</th>
              </tr>
            </thead>
            <tbody>
              {table.groups.map((g) => (
                <GroupRows key={g.group} group={g} layout={table.layout} mode={mode} headerAsOf={data.as_of} />
              ))}
              {table.groups.length === 0 && (
                <tr className="ms-empty">
                  <td colSpan={PERIODS.length + (priv ? 5 : 4)}>
                    No fund holdings for {table.title} at this month-end ({table.portfolio_codes.join(', ')}).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="ms-foot">
          {lagDates.length > 0 && (
            <div>† Returns as at {lagDates.map(longDate).join(' / ')} (fund NAV not yet available for {longDate(data.as_of)}); alpha for these funds uses the benchmark&apos;s return to the same month.</div>
          )}
          {priv && (
            <div>Sharpe ratio and standard deviation are 3-year figures{data.restated ? '; Sharpe is not restated for earlier months' : ''}.</div>
          )}
          <div>{SOURCE_NOTE}{table.note ? ` ${table.note}` : ''}</div>
          <div>Benchmark rows are the Drummond-assigned benchmark funds from the AP List; tracking error is the annualised standard deviation of monthly active return against that benchmark.</div>
          <ManagedDisclaimer dkey="tool_manager_summary" className="ms-disclaimer" />
        </div>
      </div>}
    </div>
  );
}
